// Sprint-007 D6 — WebAuthn/passkeys browser plumbing (F4-2 + F4-5, ADR-002
// Amendment 4 decision 3). This is the ONE file in the frontend that knows
// the literal base64url<->ArrayBuffer/bytes wire shapes the backend's
// `webauthn` (Duo Labs py_webauthn v3) library produces and expects — every
// other file (views, hooks) calls the high-level `enrollPasskey()`/
// `signInWithPasskey()` functions below and never touches a raw
// `PublicKeyCredential` or a base64url string.
//
// The exact JSON shapes here were verified by reading the INSTALLED
// library's source directly (not assumed from docs), matching the server
// side of the contract exactly:
//   - `.../register/begin` and `.../login/begin` return the literal
//     `options_to_json(...)` output of `webauthn.generate_registration_options`/
//     `generate_authentication_options` — base64url (no padding) for
//     `challenge`, `user.id`, and every `*Credentials[].id`; every other
//     field (pubKeyCredParams, authenticatorSelection, attestation, rpId,
//     userVerification, timeout) passes straight through untouched.
//   - `.../register/complete` and `.../login/complete` accept exactly the
//     dict shape `webauthn.helpers.parse_registration_credential_json`/
//     `parse_authentication_credential_json` parse: `{id, rawId, type,
//     response: {...}, authenticatorAttachment?}`, every byte field
//     base64url (no padding) — this is the same shape every other
//     WebAuthn JS client library (browser's own `PublicKeyCredential.
//     toJSON()`, SimpleWebAuthn's browser helpers) produces, hand-built
//     here rather than depending on the newer `toJSON()`/
//     `parseCreationOptionsFromJSON()` browser methods (Safari/older
//     Android WebViews on this audience's phones cannot be assumed to
//     have them yet — see the PR notes).
//
// NEVER use the words "passkey"/"WebAuthn"/"biometric" in any string
// exposed to the UI from this module — this file returns booleans/typed
// data only. Every user-facing string lives in `lib/authCopy.ts` (UX doc:
// "Face ID or your fingerprint," everywhere, no bare technical term).

import {
  postPasskeyLoginBegin,
  postPasskeyLoginComplete,
  postPasskeyRegisterBegin,
  postPasskeyRegisterComplete,
} from "./api";
import type { PasskeyLoginOut, PasskeyOut } from "../types";

// ---------------------------------------------------------------------------
// Feature detection
// ---------------------------------------------------------------------------

/** `window.PublicKeyCredential` existing is the baseline "this browser
 * speaks WebAuthn at all" check — every other capability below is
 * meaningless without this being true first. */
export function isWebAuthnSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential === "function" &&
    typeof navigator !== "undefined" &&
    typeof navigator.credentials !== "undefined"
  );
}

/** Whether THIS device has a built-in platform authenticator (Face ID,
 * Touch ID, Windows Hello, Android biometrics) as opposed to only
 * supporting an external security key. Informational only — this app
 * never blocks the flow on this being true (a security-key-only device is
 * still a valid, if less common, "Face ID or your fingerprint" story), but
 * it's useful for deciding whether to bother offering the one-tap UI at
 * all on a device that plainly has neither. */
export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  if (!isWebAuthnSupported()) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/** Conditional UI / autofill support (the browser's own "suggest a
 * discoverable passkey next to a username field" idiom, triggered by
 * `autocomplete="username webauthn"` + `navigator.credentials.get({
 * mediation: "conditional" })`). Where this is available, UX §2.1's
 * "passkey button goes first, above the typed form" is satisfied by the
 * platform's native suggestion chip instead of an app-drawn button —
 * where it ISN'T available, `LoginView` falls back to a visible button. */
export async function isConditionalMediationAvailable(): Promise<boolean> {
  if (!isWebAuthnSupported()) return false;
  const fn = (
    PublicKeyCredential as unknown as {
      isConditionalMediationAvailable?: () => Promise<boolean>;
    }
  ).isConditionalMediationAvailable;
  if (typeof fn !== "function") return false;
  try {
    return await fn();
  } catch {
    return false;
  }
}

/** UX §6 copy table: the Returning Login screen's biometric button reads
 * "Use Face ID" or "Use your fingerprint" "if the platform can tell."
 * There is no standard API to ask a browser "which biometric does this
 * device use," so this is a best-effort UA/platform heuristic — Android
 * devices predominantly expose fingerprint (and, on newer devices, face
 * unlock under the same "fingerprint" umbrella term Android itself uses);
 * everything else (iOS/iPadOS/macOS, and any device this heuristic can't
 * place) defaults to "Face ID," which is also the more universally
 * recognized term. Never throws; always returns a value. */
export function guessBiometricKind(): "face" | "fingerprint" {
  if (typeof navigator === "undefined") return "face";
  const ua = navigator.userAgent || "";
  return /Android/i.test(ua) ? "fingerprint" : "face";
}

// ---------------------------------------------------------------------------
// base64url (no padding) <-> ArrayBuffer/Uint8Array
// ---------------------------------------------------------------------------

function base64urlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const padLength = (4 - (base64.length % 4)) % 4;
  const padded = base64 + "=".repeat(padLength);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i] ?? 0);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// ---------------------------------------------------------------------------
// Server options JSON (options_to_json output) -> browser
// CredentialCreationOptions / CredentialRequestOptions
// ---------------------------------------------------------------------------

interface ServerDescriptorJSON {
  id: string;
  type: "public-key";
  transports?: string[];
}

interface ServerCreationOptionsJSON {
  rp: { id?: string; name: string };
  user: { id: string; name: string; displayName: string };
  challenge: string;
  pubKeyCredParams: { type: "public-key"; alg: number }[];
  timeout?: number;
  excludeCredentials?: ServerDescriptorJSON[];
  authenticatorSelection?: Record<string, unknown>;
  attestation?: string;
}

interface ServerRequestOptionsJSON {
  challenge: string;
  timeout?: number;
  rpId?: string;
  allowCredentials?: ServerDescriptorJSON[];
  userVerification?: string;
}

function toDescriptor(d: ServerDescriptorJSON): PublicKeyCredentialDescriptor {
  return {
    id: base64urlToBuffer(d.id),
    type: "public-key",
    transports: d.transports as AuthenticatorTransport[] | undefined,
  };
}

function toCreationOptions(json: ServerCreationOptionsJSON): PublicKeyCredentialCreationOptions {
  return {
    ...json,
    challenge: base64urlToBuffer(json.challenge),
    user: { ...json.user, id: base64urlToBuffer(json.user.id) },
    excludeCredentials: json.excludeCredentials?.map(toDescriptor),
  } as PublicKeyCredentialCreationOptions;
}

function toRequestOptions(json: ServerRequestOptionsJSON): PublicKeyCredentialRequestOptions {
  return {
    ...json,
    challenge: base64urlToBuffer(json.challenge),
    allowCredentials: json.allowCredentials?.map(toDescriptor),
  } as PublicKeyCredentialRequestOptions;
}

// ---------------------------------------------------------------------------
// Browser PublicKeyCredential -> the JSON dict shape
// parse_registration_credential_json / parse_authentication_credential_json
// expect (see this file's header comment)
// ---------------------------------------------------------------------------

function registrationCredentialToJSON(credential: PublicKeyCredential): Record<string, unknown> {
  const response = credential.response as AuthenticatorAttestationResponse;
  const transports =
    typeof response.getTransports === "function" ? response.getTransports() : undefined;
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: "public-key",
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      attestationObject: bufferToBase64url(response.attestationObject),
      ...(transports && transports.length ? { transports } : {}),
    },
    ...(credential.authenticatorAttachment
      ? { authenticatorAttachment: credential.authenticatorAttachment }
      : {}),
  };
}

function authenticationCredentialToJSON(credential: PublicKeyCredential): Record<string, unknown> {
  const response = credential.response as AuthenticatorAssertionResponse;
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: "public-key",
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      authenticatorData: bufferToBase64url(response.authenticatorData),
      signature: bufferToBase64url(response.signature),
      ...(response.userHandle ? { userHandle: bufferToBase64url(response.userHandle) } : {}),
    },
    ...(credential.authenticatorAttachment
      ? { authenticatorAttachment: credential.authenticatorAttachment }
      : {}),
  };
}

// ---------------------------------------------------------------------------
// Error classification
// ---------------------------------------------------------------------------

/** True for a user-initiated cancel/dismiss of the native platform prompt
 * (`NotAllowedError` is the WebAuthn spec's catch-all for "the user
 * declined" AND "no matching credential / ceremony timed out") or an
 * explicit `AbortController` abort (`AbortError` — used below to cancel an
 * in-flight conditional-UI `get()` when the person submits the typed form
 * instead, or when the screen unmounts). Both are the same silent,
 * non-alarming path UX §1.3/§2.2 specify — "no error banner at all... a
 * normal, silent path, not a failure." */
export function isCancelledCeremony(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return err.name === "NotAllowedError" || err.name === "AbortError";
}

// ---------------------------------------------------------------------------
// High-level ceremonies
// ---------------------------------------------------------------------------

/**
 * Enrollment (F4-5, UX §1.3/§4.2's "Add a quick sign-in" — the SAME flow
 * both call). Session-authed on the server side; this function is a pure
 * two-step round trip (`begin` -> `navigator.credentials.create()` ->
 * `complete`) with no UI concerns of its own — callers (`PasskeyEnrollView`)
 * own all copy/state.
 */
export async function enrollPasskey(nickname?: string | null): Promise<PasskeyOut> {
  const optionsJson = (await postPasskeyRegisterBegin()) as unknown as ServerCreationOptionsJSON;
  const options = toCreationOptions(optionsJson);
  const credential = (await navigator.credentials.create({
    publicKey: options,
  })) as PublicKeyCredential | null;
  if (!credential) throw new Error("no credential returned from navigator.credentials.create()");
  const credentialJson = registrationCredentialToJSON(credential);
  return postPasskeyRegisterComplete({ credential: credentialJson, nickname: nickname ?? null });
}

export interface SignInWithPasskeyOptions {
  /** `"conditional"` for the background autofill flow (UX §2.1's one-tap
   * placement, no typing at all); omit for an explicit button tap, which
   * uses the browser's default (modal) mediation and may show its own
   * account/credential picker. */
  mediation?: "conditional";
  /** Lets the caller cancel an in-flight conditional `get()` — e.g. when
   * the person submits the typed username/password form instead, or the
   * screen unmounts. Rejects with an `AbortError`, which
   * `isCancelledCeremony()` classifies as a silent, non-error cancel. */
  signal?: AbortSignal;
}

/**
 * One-tap sign-in (F4-2, UX §2's Returning Login screen AND the
 * conditional-UI autofill path). Pre-session on the server side — no
 * username is ever sent; the browser/authenticator resolves which
 * discoverable credential to present. On success, mints a fresh session
 * cookie exactly like `postLogin` — the caller (`LoginView`) re-fetches
 * `/me` the same way it does after a password login rather than trusting
 * this function's return value to decide the next screen.
 */
export async function signInWithPasskey(
  options?: SignInWithPasskeyOptions,
): Promise<PasskeyLoginOut> {
  const optionsJson = (await postPasskeyLoginBegin()) as unknown as ServerRequestOptionsJSON;
  const requestOptions = toRequestOptions(optionsJson);
  const credentialRequestOptions: CredentialRequestOptions = { publicKey: requestOptions };
  if (options?.mediation) {
    (credentialRequestOptions as { mediation?: string }).mediation = options.mediation;
  }
  if (options?.signal) credentialRequestOptions.signal = options.signal;

  const credential = (await navigator.credentials.get(
    credentialRequestOptions,
  )) as PublicKeyCredential | null;
  if (!credential) throw new Error("no credential returned from navigator.credentials.get()");
  const credentialJson = authenticationCredentialToJSON(credential);
  return postPasskeyLoginComplete({ credential: credentialJson });
}
